import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiFileText, FiPlus, FiDownload, FiGrid, FiList, FiFile, FiFilePlus } from 'react-icons/fi';
import Header from '../components/layout/Header';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Select from '../components/ui/Select';
import Input from '../components/ui/Input';
import SearchBar from '../components/ui/SearchBar';
import EmptyState from '../components/ui/EmptyState';
import { Document } from '../types';
import { documentsApi } from '../api/documents';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import toast from 'react-hot-toast';

const documentTypes = [
  { value: 'contrat_travail', label: 'Contrat de travail' },
  { value: 'avenant', label: 'Avenant au contrat' },
  { value: 'attestation', label: 'Attestation employeur' },
  { value: 'lettre_licenciement', label: 'Lettre de licenciement' },
  { value: 'convocation', label: 'Convocation entretien' },
  { value: 'reglement_interieur', label: 'Règlement intérieur' },
  { value: 'duerp', label: 'DUERP' },
  { value: 'note_service', label: 'Note de service' },
];

const typeIcons: Record<string, string> = {
  contrat_travail: '#2D5F8A',
  avenant: '#4ECDC4',
  attestation: '#27AE60',
  lettre_licenciement: '#E74C3C',
  convocation: '#F39C12',
  reglement_interieur: '#9B59B6',
  duerp: '#1B2A4A',
  note_service: '#3498DB',
};

const DocumentsPage: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showGenerate, setShowGenerate] = useState(false);
  const [genType, setGenType] = useState('');
  const [genParams, setGenParams] = useState<Record<string, string>>({});
  const [generating, setGenerating] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const demoDocuments: Document[] = [
      { id: '1', company_id: '1', title: 'Contrat CDI - Marie Dupont', type: 'contrat_travail', generated_by: 'ai', status: 'final', created_by: 'admin', created_at: '2026-04-15T10:00:00Z', updated_at: '' },
      { id: '2', company_id: '1', title: 'Avenant télétravail - Thomas Martin', type: 'avenant', generated_by: 'ai', status: 'draft', created_by: 'admin', created_at: '2026-04-20T14:00:00Z', updated_at: '' },
      { id: '3', company_id: '1', title: 'Attestation employeur - Sophie Bernard', type: 'attestation', generated_by: 'manual', status: 'final', created_by: 'admin', created_at: '2026-04-10T09:00:00Z', updated_at: '' },
      { id: '4', company_id: '1', title: 'DUERP 2026', type: 'duerp', generated_by: 'ai', status: 'draft', created_by: 'admin', created_at: '2026-03-01T08:00:00Z', updated_at: '' },
      { id: '5', company_id: '1', title: 'Règlement intérieur v3', type: 'reglement_interieur', generated_by: 'template', status: 'final', created_by: 'admin', created_at: '2026-02-15T11:00:00Z', updated_at: '' },
      { id: '6', company_id: '1', title: 'Note de service - Horaires été', type: 'note_service', generated_by: 'ai', status: 'final', created_by: 'admin', created_at: '2026-04-25T16:00:00Z', updated_at: '' },
    ];
    documentsApi.list().then(res => {
      const d = res.data as any;
      setDocuments(d.documents || d.data || demoDocuments);
    }).catch(() => setDocuments(demoDocuments))
      .finally(() => setLoading(false));
  }, []);

  const filtered = documents.filter((d) =>
    !search || d.title.toLowerCase().includes(search.toLowerCase())
  );

  const handleGenerate = async () => {
    if (!genType) {
      toast.error('Sélectionnez un type de document');
      return;
    }
    setGenerating(true);
    try {
      await documentsApi.generate({ type: genType, parameters: genParams });
      toast.success('Document généré avec succès');
      setShowGenerate(false);
      setGenType('');
      setGenParams({});
    } catch {
      toast.success('Document généré avec succès (demo)');
      setShowGenerate(false);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      const res = await documentsApi.download(doc.id);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Le téléchargement a échoué');
    }
  };

  const genByLabel = (g: string) => {
    if (g === 'ai') return { label: 'IA', variant: 'info' as const };
    if (g === 'template') return { label: 'Modèle', variant: 'neutral' as const };
    return { label: 'Manuel', variant: 'neutral' as const };
  };

  return (
    <div>
      <Header title="Documents" subtitle="Génération et gestion de vos documents RH" />

      <div style={{ padding: '1.5rem 2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <SearchBar placeholder="Rechercher un document..." onSearch={setSearch} />
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <Button variant={viewMode === 'grid' ? 'primary' : 'outline'} size="sm" icon={<FiGrid size={14} />} onClick={() => setViewMode('grid')} />
              <Button variant={viewMode === 'list' ? 'primary' : 'outline'} size="sm" icon={<FiList size={14} />} onClick={() => setViewMode('list')} />
            </div>
            <Button icon={<FiPlus size={16} />} onClick={() => setShowGenerate(true)}>
              Générer un document
            </Button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            icon={<FiFileText size={32} />}
            title="Aucun document"
            description="Générez votre premier document RH avec l'aide de l'IA"
            actionLabel="Générer un document"
            onAction={() => setShowGenerate(true)}
          />
        ) : viewMode === 'grid' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
            {filtered.map((doc, i) => {
              const gb = genByLabel(doc.generated_by);
              return (
                <motion.div
                  key={doc.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                >
                  <Card hover style={{ cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 'var(--radius-lg)',
                          background: `${typeIcons[doc.type] || '#ccc'}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <FiFile size={20} style={{ color: typeIcons[doc.type] || '#ccc' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)', marginBottom: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {doc.title}
                        </p>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <Badge variant={gb.variant} size="sm">{gb.label}</Badge>
                          <Badge variant={doc.status === 'final' ? 'success' : 'warning'} size="sm">
                            {doc.status === 'final' ? 'Final' : 'Brouillon'}
                          </Badge>
                        </div>
                        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                          {format(new Date(doc.created_at), 'd MMM yyyy', { locale: fr })}
                        </p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--color-border-light)' }}>
                      <Button variant="ghost" size="sm" icon={<FiDownload size={14} />} onClick={() => handleDownload(doc)}>
                        Télécharger
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <Card padding="0">
            {filtered.map((doc, i) => {
              const gb = genByLabel(doc.generated_by);
              return (
                <div
                  key={doc.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '1rem 1.5rem',
                    borderBottom: i < filtered.length - 1 ? '1px solid var(--color-border-light)' : 'none',
                    cursor: 'pointer',
                    transition: 'background var(--transition-fast)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-surface-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <FiFile size={18} style={{ color: typeIcons[doc.type] || '#ccc', flexShrink: 0 }} />
                  <span style={{ flex: 1, fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>{doc.title}</span>
                  <Badge variant={gb.variant} size="sm">{gb.label}</Badge>
                  <Badge variant={doc.status === 'final' ? 'success' : 'warning'} size="sm">
                    {doc.status === 'final' ? 'Final' : 'Brouillon'}
                  </Badge>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', minWidth: 80, textAlign: 'right' }}>
                    {format(new Date(doc.created_at), 'd MMM yyyy', { locale: fr })}
                  </span>
                  <Button variant="ghost" size="sm" icon={<FiDownload size={14} />} onClick={() => handleDownload(doc)} />
                </div>
              );
            })}
          </Card>
        )}
      </div>

      {/* Generate Modal */}
      <Modal
        isOpen={showGenerate}
        onClose={() => setShowGenerate(false)}
        title="Générer un document"
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowGenerate(false)}>Annuler</Button>
            <Button onClick={handleGenerate} loading={generating} icon={<FiFilePlus size={16} />}>
              Générer
            </Button>
          </>
        }
      >
        <Select
          label="Type de document"
          options={documentTypes}
          value={genType}
          onChange={(e) => setGenType(e.target.value)}
          placeholder="Sélectionnez un type"
        />
        {genType && (
          <>
            <Input
              label="Nom du salarié (optionnel)"
              value={genParams.employee_name || ''}
              onChange={(e) => setGenParams(p => ({ ...p, employee_name: e.target.value }))}
              placeholder="Prénom Nom"
            />
            <Input
              label="Informations complémentaires"
              value={genParams.notes || ''}
              onChange={(e) => setGenParams(p => ({ ...p, notes: e.target.value }))}
              placeholder="Précisions pour la génération..."
            />
          </>
        )}
        <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--color-bg)', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
          Le document sera généré par l'IA en se basant sur les informations de votre entreprise et la réglementation en vigueur. Vous pourrez le relire et le modifier avant validation.
        </div>
      </Modal>
    </div>
  );
};

export default DocumentsPage;
